import { Theme } from '@eversports/react-components/design-tokens/theme'
import Collapse from '@eversports/react-components/primitives/Collapse'
import Flex from '@eversports/react-components/primitives/Flex'
import Icon, { Icons } from '@eversports/react-components/primitives/Icon'
import Text from '@eversports/react-components/primitives/Text'
import { makeStyles } from '@mui/styles'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Routes from '../data/routes'

import { Localized } from '../localization/react'

const useStyles = makeStyles((theme: Theme) => ({
  chevronContainer: ({ isExpanded }: { isExpanded: boolean }) => ({
    transform: isExpanded ? `rotate(0)` : `rotate(180deg)`,
    transition: `all ${theme.transitions.duration.short}ms`,
  }),
  sectionContainer: {
    boxShadow: theme.customShadows.boxShadowUp,
    maxHeight: '100vh',
    position: 'fixed',
    width: '100%',
    zIndex: 2,
    bottom: 0,
    left: 0,
  },
  collapsibleContentContainer: {
    overflowY: 'auto',
    padding: `0 ${theme.layoutSpacing.default}`,
    backgroundColor: theme.palette.white,
  },
  collapsibleContent: {
    [theme.breakpoints.down('sm')]: {
      overflowY: 'auto',
    },
  },
  overlay: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: -1,
    position: 'fixed',
    touchAction: 'none',
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
  },
  expandDetailsButton: {
    backgroundColor: theme.palette.text.primary,
    opacity: 0.7,
  },
  bottomHeader: {
    padding: `0 ${theme.layoutSpacing.default} ${theme.layoutSpacing.default} ${theme.layoutSpacing.default}`,
    backgroundColor: theme.palette.white,
  },
}))

interface Props {
  collapsibleContent: React.ReactNode
  showExpandIcon: boolean
  hideHeaderOnExpand?: boolean
}

const BottomFixedCollapsibleSection: React.FC<React.PropsWithChildren<Props>> = ({
  showExpandIcon,
  collapsibleContent,
  hideHeaderOnExpand,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const classes = useStyles({ isExpanded })
  const location = useLocation()

  // Reset the expandable section whenever we change routes except if:
  // a) We enter the review information page and the collapsible is closed
  // expand it, so the user can see what he is reviewing
  useEffect(() => {
    if (location.pathname === Routes.REVIEW_INFORMATION && !isExpanded) {
      setIsExpanded(true)
    } else {
      setIsExpanded(false)
    }
  }, [location])

  return (
    <Flex flexDirection="column" className={classes.sectionContainer}>
      {isExpanded && <div className={classes.overlay} onClick={() => setIsExpanded(false)} />}
      {showExpandIcon && (
        <>
          <Flex
            justifyContent="center"
            alignItems="center"
            horizontalSpacing="dense"
            onClick={() => setIsExpanded(!isExpanded)}
            className={classes.expandDetailsButton}
            fullWidth
          >
            <Text size="small" color="white">
              {!isExpanded ? <Localized id="expand-details" /> : <Localized id="collapse-details" />}
            </Text>
            <span className={classes.chevronContainer}>
              <Icon icon={Icons.EXPAND_MORE} color="white" size="x-large" />
            </span>
          </Flex>
        </>
      )}
      <div className={classes.collapsibleContentContainer}>
        <Flex flexDirection="column">
          <Collapse in={isExpanded}>
            <Flex
              flexDirection="column"
              verticalSpacing="wide"
              marginBottom="default"
              className={classes.collapsibleContent}
            >
              {collapsibleContent}
            </Flex>
          </Collapse>
        </Flex>
      </div>
      {hideHeaderOnExpand && isExpanded ? null : <div className={classes.bottomHeader}>{children}</div>}
    </Flex>
  )
}

export default BottomFixedCollapsibleSection
